/**
 * A set of overriden Element from elements.js to handle custom ID and such
 */

import { Context, Toolkit } from "plano_engine";
import * as d3 from "d3";

function rasterize(svg, width, height) {
    return new Promise(resolve => {
        Toolkit.rasterize({ node: svg }, width, height, resolve);
    });
}

export function stripSvg(svg) {
    return svg.cloneNode(true);
}

export const CUSTOM_EVENTS = {
    applyColorTester: "applyColorTester",
    openHighlightMenu: "openHighlightMenu"
};

export const NONE_HIGHLIGHT = { id: null, code: "#fff", label: "None" };

// No need to import BoardImageModule and similar, since they are defined in the window

// DO NOT RENAME / EXTRACT OF WINDOW
// The plano engine depends that this class has this exact name and be declared on window.
window.BoardModuleElement = class BoardModuleElement extends BoardImageModule {
    static create(moduleId, { shadeByGroup, highlightTester, label, width, height, imageURL, realisticURL }) {
        return new BoardModuleElement(moduleId, {
            shadeByGroup,
            highlightTester,
            label,
            width: width * Context.rescaleFactor,
            height: height * Context.rescaleFactor,
            imageURL,
            realisticURL
        });
    }

    // This method will be dynamically called using `window["BoardModuleElement"].build(...)
    static build(spec, models) {
        let model = models.get(spec.model);
        const { label, width, height, imageURL: imageURL, realisticURL: realisticURL } = model;
        const { shadeByGroup, highlightTester } = spec;

        return BoardModuleElement.create(model.id, { label, width, height, imageURL, realisticURL, shadeByGroup, highlightTester });
    }

    constructor(moduleId, { shadeByGroup, highlightTester, label, width, height, imageURL, realisticURL }) {
        super(label, width, height, imageURL, realisticURL);
        this._moduleId = moduleId;
        this._shadeByGroup = {};
        this._highlightTester = {};
        // Sometimes the back may mistakenly convert `{}` to `[]`
        // so we purposefully revert the change
        if (shadeByGroup && !Array.isArray(shadeByGroup)) {
            this._shadeByGroup = shadeByGroup;
        }

        // Same problem for the highlight Testers Object
        if (highlightTester && !Array.isArray(highlightTester)) {
            this._highlightTester = highlightTester;
        }
    }

    get shadeByGroup() {
        return this._shadeByGroup;
    }

    set shadeByGroup(value) {
        this.register();
        this._shadeByGroup = value || {};
    }
    get highlightTester() {
        return this._highlightTester;
    }
    set highlightTester(testers) {
        this.register();
        this._highlightTester = testers || {};
    }

    modelId() {
        return this._moduleId;
    }

    memento() {
        const res = super.memento();
        res.shadeByGroup = this.shadeByGroup;
        res.highlightTester = this.highlightTester;
        return res;
    }

    revert(memento) {
        super.revert(memento);
        this.shadeByGroup = memento.shadeByGroup;
        this.highlightTester = memento.highlightTester;
    }

    get type() {
        return "ModuleElement";
    }

    serializeSpec() {
        return { ...super.serializeSpec(), shadeByGroup: this.shadeByGroup, highlightTester: this.highlightTester };
    }

    duplicate() {
        const { width, height, imageURL, realisticURL, shadeByGroup, highlightTester } = this;
        let copy = new BoardModuleElement(this._moduleId, {
            label: this.label,
            width,
            height,
            imageURL,
            realisticURL,
            shadeByGroup,
            highlightTester
        });
        if (this.frImage.children().length > 0) {
            copy.changeImage(this.frImage.node, this.elImage && this.elImage.node);
        }
        if (this.shadeColorsImage) {
            copy.shadeColorsImage = this.shadeColorsImage.cloneNode();
        }
        if (this.shadeReferenceImage) {
            copy.shadeReferenceImage = this.shadeReferenceImage.cloneNode();
        }
        return copy;
    }

    copy(elements) {
        let copy = super.copy(elements);
        copy.status = this.status;
        if (copy.status) {
            copy.addDecoration(assets.icons.status[copy.status.name], 3);
        }
        return copy;
    }

    async rasterisePreview(allGroups, shades) {
        if (!this.svg) return;
        const { root, leaf } = createRasteriserTemplate();
        document.body.append(root);

        const copyApplyAndRasterize = async cb => {
            const previewSvgClone = updateSvgID(stripSvg(this.svg.cloneNode(true)));

            try {
                leaf.append(previewSvgClone);

                const { width, height } = Snap(previewSvgClone).attr("viewBox");

                previewSvgClone.setAttribute("width", width);
                previewSvgClone.setAttribute("height", height);
                previewSvgClone.removeAttribute("viewBox");

                // scale down the image to the to the module size
                const bbox = previewSvgClone.getBoundingClientRect();

                let xFactor = this.width / bbox.width;
                let yFactor = this.height / bbox.height;

                cb(previewSvgClone);

                Snap(leaf).transform(new Snap.Matrix().scale(xFactor, yFactor, 0, 0));

                return await rasterize(leaf, width, height);
            } finally {
                leaf.removeAttribute("transform");
                previewSvgClone.remove();
            }
        };

        try {
            this.shadeColorsImage = await copyApplyAndRasterize(previewSvgClone => {
                applyShadesOnSvg(previewSvgClone, this.shadeByGroup, allGroups, shades);
            });

            if (hasShadeWithTester(this.shadeByGroup, shades, allGroups)) {
                this.shadeReferenceImage = await copyApplyAndRasterize(previewSvgClone => {
                    applyReferencesOnSvg(previewSvgClone, this.shadeByGroup, allGroups, shades, this.highlightTester);
                    trimModuleToKeepTestersOnly(previewSvgClone);
                });
            } else {
                this.shadeReferenceImage = undefined;
            }
            this.prepareImage();
        } finally {
            root.remove();
        }
    }

    get svg() {
        const cache = Context.svgImages[this.imageURL];
        const node = cache && cache.node;
        return node instanceof DocumentFragment ? node.querySelector("svg") : node;
    }
};

function hasShadeWithTester(shadeByGroup, shades, allGroups) {
    const allShades = deserializeShades(shadeByGroup, shades);
    for (const rowId in allShades) {
        const group = allGroups.find(group => group.rowId === rowId);
        if (group && group.tester) {
            return true;
        }
    }
    return false;
}

function parseShadeNumnber(properties) {
    if (Array.isArray(properties)) {
        const property = properties.find(property => property.propertyType.id === 6);
        return property && property.value;
    } else {
        return properties && properties.number && properties.number.numb1 && properties.number.numb1.value;
    }
}
const defaultShadeColor = "#aaaaaa";

/**
 *
 * @param preview {SVGElement}
 * @param group
 * @param shade
 * @param highlightTester {tester: {color:string, rowId: string }}
 */
function setTester(preview, group, shade, highlightTester) {
    const shadeNumber = shade && shade.properties && parseShadeNumnber(shade.properties);
    const text = findTesterText(preview, group.tester);
    if (text) text.remove(text);

    // we calculate the position of the tester within the <svg>
    // then insert a text with a proper x/w to appear on the top of the tester.
    // we also add a custom `tester` attribute on the text so that we can properly clean it up on the following renders

    const tester = d3.select(preview).select("#" + group.tester);

    if (tester.empty()) return;

    if (shade) {
        const previewBBox = preview.getBoundingClientRect();
        tester.style("fill", `white`);

        // Snap implementation of getBBox is better than the official getBoundingClientRect.
        // it directly gives the local position of the element within the <svg>
        const testerBBox = tester.node().getBoundingClientRect();
        const text = createTextElement(
            shadeNumber,
            testerBBox.left - previewBBox.left + (testerBBox.right - testerBBox.left) / 2,
            testerBBox.top - previewBBox.top + (testerBBox.bottom - testerBBox.top) / 2,
            group.tester,
            group.rowId
        );

        const hlTesterGroup = highlightTester && highlightTester[group.tester];
        /** @type String. Color Code **/
        let highlightColor = undefined;

        if (
            hlTesterGroup &&
            hlTesterGroup.color &&
            hlTesterGroup.color.code &&
            hlTesterGroup.color.id &&
            hlTesterGroup.color.id !== NONE_HIGHLIGHT.id
        ) {
            highlightColor = hlTesterGroup.color.code;
        } else {
            if (hlTesterGroup) {
                delete highlightTester[group.tester];
            }
            highlightColor = "#000000";
        }

        setTesterTextColor(text, highlightColor);

        text.addEventListener("contextmenu", openHighlightTestersMenuListener);
        preview.append(text);

        const availableSize = Math.min(testerBBox.right - testerBBox.left, testerBBox.bottom - testerBBox.top);
        d3.select(text).style("font-size", `${availableSize / 3}px`);
    } else {
        tester.style("fill", `#aaa`);
    }
}

function setSlotColor(svg, slotId, color) {
    d3.select(svg)
        .select("#" + slotId)
        .style(`fill`, color || defaultShadeColor);
}

function findTesterText(svg, testerId) {
    return [...svg.children].find(node => {
        return node.tagName === "text" && node.getAttribute && node.getAttribute("tester") === testerId;
    });
}

/**
 * Listener event to open the Highlight menu when clicked on text tester
 * @param e
 */
function openHighlightTestersMenuListener(e) {
    e.preventDefault();
    if (e.button !== 2) return;
    let event = new Event(CUSTOM_EVENTS.openHighlightMenu);
    event.pos = { x: e.pageX, y: e.pageY };
    event.textNode = e.target;
    window.dispatchEvent(event);
}

/**
 * Set the color on the tester text when we got an highlight param
 * @param text
 * @param color
 */
export function setTesterTextColor(text, color) {
    console.assert(text && color, "Corrupted textNode or color for setTesterTextColor");
    d3.select(text).style("fill", color);
}

function deserializeShades(shadeIdByGroup, shadesPerProduct) {
    const res = {};
    for (const key of Object.keys(shadeIdByGroup)) {
        const shadeId = Number.parseInt(shadeIdByGroup[key], 10);
        for (const product of shadesPerProduct) {
            const value = product.shades.find(shade => shade.id === shadeId);
            if (value) {
                res[key] = value;
                break;
            }
        }
        if (!res[key]) {
            console.warn("cant find shade", key, shadeId, shadesPerProduct);
        }
    }
    return res;
}

function createTextElement(text, x, y, testerId, rowId = "no_row") {
    const textElement = document.createElementNS("http://www.w3.org/2000/svg", "text");
    textElement.setAttributeNS(null, "x", x);
    textElement.setAttributeNS(null, "y", y);
    textElement.setAttributeNS(null, "text-anchor", "middle");
    textElement.setAttributeNS(null, "tester", testerId);
    textElement.setAttributeNS(null, "rowId", rowId);

    textElement.appendChild(document.createTextNode(text));
    return textElement;
}

/**
 * replace with <svg> ID with the one used by the module editor
 */
export function updateSvgID(svg) {
    d3.select(svg)
        .select("g#FILL")
        .selectAll("path")
        .select((d, i, cmps) => {
            d3.select(cmps[i]).attr("id", "path_" + i);
        });
    return svg;
}

function parseColor(properties) {
    if (Array.isArray(properties)) {
        const property = properties.find(property => property.propertyType.id === 5);
        return property && property.value;
    } else {
        return properties.colors.color.value;
    }
}

export function applyShadesOnSvg(svg, shadeIdPerGroup, allGroups, shadesPerProduct) {
    const shadePerGroup = deserializeShades(shadeIdPerGroup, shadesPerProduct);

    for (const group of allGroups) {
        const groupId = group.rowId;
        const color = shadePerGroup[groupId] && parseColor(shadePerGroup[groupId].properties);

        for (const slotId of group.modules) {
            setSlotColor(svg, slotId, color);
        }
    }

    return svg;
}

export function applyReferencesOnSvg(svg, shadeIdPerGroup, allGroups, shadesPerProduct, highlightTester) {
    const shadePerGroup = deserializeShades(shadeIdPerGroup, shadesPerProduct);

    for (const group of allGroups) {
        const groupId = group.rowId;
        setTester(svg, group, shadePerGroup[groupId], highlightTester);
    }

    return svg;
}

export function trimModuleToKeepTestersOnly(/** @type {Element} */ svg) {
    // since we called applyShadesOnSvg before, labels are present in the svg
    // we can therefore use them as an easy search for testers
    /** @type {Array<Element>} */
    const labels = [...svg.querySelectorAll("text")].filter(e => e.getAttribute("tester"));

    if (labels.length === 0) return;

    /** @type {Array<Element>} */
    const testers = [...svg.querySelectorAll(labels.map(e => `#${e.getAttribute("tester")}`).join(","))];

    return removeEverythingBut([...testers, ...labels], svg);
}

function removeEverythingBut(elements, root) {
    /** @type {Set<Element>} */
    const shouldKeepNodes = new Set();
    shouldKeepNodes.add(root);
    for (const node of elements) {
        for (let i = node; i !== root; i = i.parentNode) {
            shouldKeepNodes.add(i);
        }
    }

    /** @type {Element[]} */
    const untreatedNodes = [root];
    while (untreatedNodes.length > 0) {
        const node = untreatedNodes[0];
        untreatedNodes.splice(0, 1);

        if (shouldKeepNodes.has(node)) {
            untreatedNodes.push(...node.children);
        } else {
            node.remove();
        }
    }
}

function cloneWithLeaves(/** @type {Element} */ svg, /** @type {Element[]} */ items) {
    const root = svg.cloneNode();
    const nodeCloneCache = new Map();

    for (const node of items) {
        // path from top to bottom: first preview child -> leaves
        const path = [];
        for (let i = node; i !== svg; i = i.parentNode) {
            path.splice(0, 0, i);
        }

        // we clone each element of the path once, and reconstruct the tree
        let previousElement = root;
        for (const element of path) {
            /** @type {Element} */
            let elementClone = nodeCloneCache.get(element);
            if (!elementClone) {
                elementClone = element.cloneNode();
                nodeCloneCache.set(element, elementClone);
                previousElement.appendChild(elementClone);

                if (elementClone instanceof SVGTextPositioningElement) {
                    elementClone.textContent = element.textContent;
                }
            }
            previousElement = elementClone;
        }
    }
    return root;
}

// we create the following template:
// <div><svg><g></g></svg></div><
// the <g> is necessary for the trannsform
// the <svg> is used to contain the <g>
function createRasteriserTemplate() {
    const placeholder = document.createElement("div");
    const svgElem = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    placeholder.append(svgElem);
    const gElem = document.createElementNS("http://www.w3.org/2000/svg", "g");
    svgElem.append(gElem);

    return {
        root: placeholder,
        leaf: svgElem
    };
}



// WEBPACK FOOTER //
// ./src/plano/elements.js